// 所有请求都从这里走：相对地址 /api/v1（与旧界面同源，由前端容器的 nginx 转给后端），
// 带上令牌与所选空间；401 时用刷新令牌换一次新令牌并重试，换不到就退出登录。

import { clearSession, getRefreshToken, getSelectedTenantId, getToken, setTokens } from './session'

export const API_BASE = '/api/v1'

export class ApiError extends Error {
  readonly status: number
  readonly code?: string | number
  readonly data?: unknown
  constructor(message: string, status: number, code?: string | number, data?: unknown) {
    super(message)
    this.name = 'ApiError'
    this.status = status
    this.code = code
    this.data = data
  }
}

/** 退出登录时通知界面（回到登录页）。 */
export const sessionEvents = new EventTarget()
export const SESSION_EXPIRED = 'lingzhi:session-expired'

const PUBLIC = /^(\/auth\/(login|register|refresh|config|oidc\/)|\/hub\/feishu\/(auto-login|bind|login))/

const requestId = () => Math.random().toString(36).slice(2, 14).padEnd(12, '0')

export function authHeaders(): Record<string, string> {
  const headers: Record<string, string> = { 'Accept-Language': 'zh-CN', 'X-Request-ID': requestId() }
  const token = getToken()
  if (token) headers.Authorization = `Bearer ${token}`
  const tenant = getSelectedTenantId()
  if (tenant) headers['X-Tenant-ID'] = tenant
  return headers
}

/** 服务端的错误有三种写法：{error:"…"}、{error:{message}}、{message}。 */
export function errorMessage(data: unknown, fallback: string): string {
  if (data && typeof data === 'object') {
    const d = data as { error?: unknown; message?: unknown }
    if (typeof d.error === 'string' && d.error) return d.error
    if (d.error && typeof d.error === 'object' && typeof (d.error as { message?: unknown }).message === 'string') return (d.error as { message: string }).message
    if (typeof d.message === 'string' && d.message) return d.message
  }
  return fallback
}

const STATUS_TEXT: Record<number, string> = {
  400: '请求有误',
  401: '登录已过期，请重新登录',
  403: '没有权限',
  404: '找不到了',
  409: '已经存在',
  413: '文件太大了',
  429: '请求太频繁，请稍后再试',
  500: '服务器出错了',
  502: '服务器暂时连不上',
  503: '服务器暂时不可用',
}

let refreshing: Promise<boolean> | null = null

/** 同一时间只刷新一次：并发的 401 都等这一次的结果。 */
export function refreshSession(): Promise<boolean> {
  refreshing ??= (async () => {
    const refreshToken = getRefreshToken()
    if (!refreshToken) return false
    try {
      const res = await fetch(`${API_BASE}/auth/refresh`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Accept-Language': 'zh-CN' },
        body: JSON.stringify({ refreshToken }),
      })
      const data = (await res.json().catch(() => null)) as { access_token?: string; refresh_token?: string } | null
      if (!res.ok || !data?.access_token) return false
      setTokens(data.access_token, data.refresh_token)
      return true
    } catch {
      return false
    } finally {
      queueMicrotask(() => (refreshing = null))
    }
  })()
  return refreshing
}

export function expireSession() {
  clearSession()
  sessionEvents.dispatchEvent(new Event(SESSION_EXPIRED))
}

export interface RequestOptions {
  method?: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE'
  query?: Record<string, string | number | boolean | undefined | null | string[]>
  body?: unknown
  signal?: AbortSignal
  /** 返回原始 Response（流式、二进制）。 */
  raw?: boolean
  headers?: Record<string, string>
}

export function buildUrl(path: string, query?: RequestOptions['query']): string {
  // /files 是灵智原生的受保护文件代理；/studio/ 是 lingzhi-studio（思考室等）。同一个域名下由 nginx 转发
  const url = path.startsWith('/files') || path.startsWith('/studio/') || path.startsWith('/hub/') ? path : `${API_BASE}${path}`
  if (!query) return url
  const params = new URLSearchParams()
  for (const [k, v] of Object.entries(query)) {
    if (v === undefined || v === null) continue
    params.set(k, Array.isArray(v) ? v.join(',') : String(v))
  }
  const s = params.toString()
  return s ? `${url}?${s}` : url
}

async function send(path: string, opts: RequestOptions): Promise<Response> {
  const isForm = typeof FormData !== 'undefined' && opts.body instanceof FormData
  const headers: Record<string, string> = { ...authHeaders(), ...opts.headers }
  if (opts.body !== undefined && !isForm) headers['Content-Type'] = 'application/json'
  return fetch(buildUrl(path, opts.query), {
    method: opts.method ?? (opts.body === undefined ? 'GET' : 'POST'),
    headers,
    body: opts.body === undefined ? undefined : isForm ? (opts.body as FormData) : JSON.stringify(opts.body),
    signal: opts.signal,
  })
}

export async function request<T = unknown>(path: string, opts: RequestOptions = {}): Promise<T> {
  let res = await send(path, opts)
  if (res.status === 401 && !PUBLIC.test(path)) {
    if (await refreshSession()) res = await send(path, opts)
    if (res.status === 401) {
      expireSession()
      throw new ApiError(STATUS_TEXT[401], 401)
    }
  }
  if (opts.raw) {
    if (!res.ok) {
      const data = await res.json().catch(() => null)
      throw new ApiError(errorMessage(data, STATUS_TEXT[res.status] ?? `请求失败（${res.status}）`), res.status, undefined, data)
    }
    return res as T
  }
  const data = res.status === 204 ? null : await res.json().catch(() => null)
  if (!res.ok || (data && typeof data === 'object' && (data as { success?: boolean }).success === false)) {
    const code = data && typeof data === 'object' ? ((data as { code?: string | number }).code ?? (data as { error?: { code?: number } }).error?.code) : undefined
    throw new ApiError(errorMessage(data, STATUS_TEXT[res.status] ?? `请求失败（${res.status}）`), res.status, code, data)
  }
  return data as T
}

/** 上传要进度条，fetch 给不了，用 XHR；同样带令牌，401 时刷新一次。 */
export function upload<T>(path: string, form: FormData, onProgress?: (fraction: number) => void, retried = false): Promise<T> {
  return new Promise<T>((resolve, reject) => {
    const xhr = new XMLHttpRequest()
    xhr.open('POST', buildUrl(path))
    for (const [k, v] of Object.entries(authHeaders())) xhr.setRequestHeader(k, v)
    xhr.upload.onprogress = (e) => {
      if (e.lengthComputable) onProgress?.(e.loaded / e.total)
    }
    xhr.onerror = () => reject(new ApiError('网络出错了，没传上去', 0))
    xhr.onload = async () => {
      let data: unknown = null
      try {
        data = JSON.parse(xhr.responseText)
      } catch {
        // 不是 JSON
      }
      if (xhr.status === 401 && !retried) {
        if (await refreshSession()) return upload<T>(path, form, onProgress, true).then(resolve, reject)
        expireSession()
        return reject(new ApiError(STATUS_TEXT[401], 401))
      }
      if (xhr.status >= 200 && xhr.status < 300) return resolve(data as T)
      const code = data && typeof data === 'object' ? (data as { code?: string }).code : undefined
      reject(new ApiError(errorMessage(data, STATUS_TEXT[xhr.status] ?? `上传失败（${xhr.status}）`), xhr.status, code, data))
    }
    xhr.send(form)
  })
}
